import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { InsertImpactOversightRatingMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type InsertImpactOversightRatingInput = {
  OversightAssessmentId: string;
  RatedItemId: string;
  Ratings: { ImpactId: string; Rating: number }[];
  TestDate: string;
  Likelihood?: number | null;
  CompletedBy?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL = (trpcData: {
  Ids: string[];
}): InsertImpactOversightRatingMutation => ({
  insertChildImpactOversightRating: {
    __typename: 'insertChildImpactRatingOutput',
    Ids: trpcData.Ids,
  },
});

export const useInsertImpactOversightRatingTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.assessment.insertImpactOversightRating.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          // Apollo parity: evictField(cache, 'impact_oversight_rating') — the
          // compound + register + by-id reads that surface the new rows.
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.assessment.oversightResultById.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.impactRating.oversightImpactRatingById.pathKey(),
          }),
          // Aggregate parity: evictField(cache, 'impact_oversight_rating') count.
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
          }),
          // Apollo parity: evictField(cache, 'oversight_assessment')
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightActivityRegister.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.oversightActivityCount.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertImpactOversightRating: async (
      variables: InsertImpactOversightRatingInput
    ): Promise<InsertImpactOversightRatingMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
